import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Breadcrumbs,
  FormatCurrencyForDisplay,
  SkeletonBase,
} from '@unified-checkout/design-system';

import { useLangStore } from '@app/dashboard/store/lang';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';

import type { ISingleBillHeader } from '@app/dashboard/types/innerLayout/bills/single-bill-header';

import styles from './header.module.scss';

export const Header = ({ data, isLoading, isError }: ISingleBillHeader) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useLangStore();

  const { id, amount, currency, status, date } = data || {};

  const getBadgeProps = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'paid':
        return { text: t('innerLayout.billsPage.table.body.status.success'), color: 'green' };
      case "pending":
        return { text: t('innerLayout.billsPage.table.body.status.pending'), color: 'yellow' };
      default:
        return { text: t("innerLayout.billsPage.table.body.status.failed"), color: 'red' };
    }
  };

  return (
    !isError && (
      <div className={styles['header']}>
        <div className={styles['header__breadcrumbs']}>
          {isLoading ? (
            <SkeletonBase height="1.5rem" />
          ) : (
            <Breadcrumbs
              linksProps={[
                {
                  text: t('innerLayout.navbar.bills'),
                  onClickHandler: () => navigate(INNER_ROUTES.BILLS),
                  active: true,
                },
                {
                  text: t('innerLayout.billsPage.singleBillPage.header.title', { id }).replace('{{id}}', id),
                },
              ]}
            />
          )}
        </div>

        {isLoading ? (
          <SkeletonBase height="3.75rem" />
        ) : (
          <div className={styles['header__main-info-box']}>
            <div className={styles['header__main-info-box__info']}>
              <div className={styles['header__main-info-box__info__amount-status-box']}>
                <span>
                  {amount && (
                    <>
                      {lang === 'en' && FormatCurrencyForDisplay(currency)}{' '}
                      {amount}
                      {' '}
                      {lang === 'ar' && FormatCurrencyForDisplay(currency)}
                    </>
                  )}
                </span>

                <Badge size="md" {...(getBadgeProps(status) as any)} />
              </div>

              <div className={styles['header__main-info-box__info__date']}>
                {`${t('innerLayout.ordersPage.singleOrdersPage.created')} ${date}`}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  );
};
